<script>
	import { preventDefault } from "svelte/legacy";
	import { params } from "@roxi/routify";
	import userService from "../user/service.svelte.js";
	import { notificate } from "../../services/notification.js";
	import { translate403Errors } from "../../services/api.svelte.js";
	import { form as svForm, field } from "svelte-forms";
	import { required, email } from "svelte-forms/validators";
	import Footer from "../_footer/index.svelte";
	import { onMount, tick } from "svelte";
	import { format, locale } from "svelte-i18n";
	import API from "../../services/api.svelte.js";
	// 1. IMPORTACIÓN DE ALTCHA
	import "altcha";

	// 2. REFERENCIA PARA EL CAPTCHA INVISIBLE
	let widgetRef = $state(null);
	let isCaptchaVerified = $state(false);

	function handleCaptchaState(event) {
		isCaptchaVerified = event.detail.state === "verified";
	}

	let altchaConfig = $derived(
		JSON.stringify({
			hideLogo: true,
			hideFooter: true,
			strings: {
				error: $format("altcha.error") || "Error",
				expired: $format("altcha.expired") || "Expirado",
				label: $format("altcha.label") || "Verificando...", // Since you use auto="onload"
				verified: $format("altcha.verified") || "Verificado",
				verifying: $format("altcha.verifying") || "Verificando...",
				waitAlert:
					$format("altcha.waitAlert") || "Por favor, aguarde...",
			},
		}),
	);

	let error = $state(null);
	let is_suspended = $state(false);
	let showLinkMessage = $state(false);

	if ($params.errors) {
		error = translate403Errors($params.errors);
	}
	if ($params.workspace === "suspended") {
		is_suspended = true;
	}

	let formIsSubmitted = false;
	let formApiErrors = $state({});

	const username = field("username", "", [required(), email()]);
	const myForm = svForm(username);

	function isSafeRelativeUrl(url) {
        if (typeof url !== "string" || !url) {
            return false;
        }

        // 1. Enforce strict relative structure first
        if (!url.startsWith("/")) {
            return false;
        }
        if (url.startsWith("//") || url.startsWith("/\\")) {
            return false;
        }

        try {
            // 2. Try to parse it as an absolute URL
            new URL(url);

            // If execution reaches here, the URL has a scheme (http:, javascript:, etc.)
            // Therefore, it is absolute and must be rejected.
            return false;
        } catch (error) {
            // 3. It threw an error because it lacked a scheme.
            // Since it already passed our startsWith('/') check, it's a safe relative path.
            return true;
        }
    }

	let queryString = new URLSearchParams(
        document.location.search.substring(1),
    );

    let raw_next_page = queryString.get("next");

    let next_page = isSafeRelativeUrl(raw_next_page) ? raw_next_page : null;

	function shouldShowFieldError(field) {
		return (
			((formIsSubmitted || field.dirty) && !field.valid) ||
			formApiErrors[field]
		);
	}

	function handlePopstate(event) {
		// If the user clicks back while the message is showing, hide it
		if (showLinkMessage) {
			showLinkMessage = false;
		}
	}

	let handleSubmit = async () => {
		formIsSubmitted = true;
		formApiErrors = {};
		if (error) error = null;

		await myForm.validate();

		if (!$myForm.valid) {
			notificate("422");
			return false;
		}

		// 3. SE EXTRAE EL TOKEN DEL CAPTCHA INVISIBLE
		const captcha_token =
			widgetRef?.value ||
			document.querySelector('input[name="altcha"]')?.value;

		if (!captcha_token) {
			// Si el usuario le da click extremadamente rápido antes de resolver el PoW
			error = $format("altcha.security_verification");
			return false;
		}

		try {
			let response = await userService.login(
				$username.value,
				captcha_token,
				next_page,
			);

			if (response == "ok") {
				showLinkMessage = true;
				history.pushState({ messageShown: true }, "", document.URL);
			}
			if (error) error = null;
		} catch (errors) {
			formApiErrors.login_failed = true;
			// Si el login falla (ej: por captcha expirado), reiniciamos el widget para el próximo intento
			widgetRef?.reset();
			isCaptchaVerified = false;
			error = null;
			return false;
		}
	};

	onMount(async () => {
		if (document.URL.indexOf("login?session_expired") != -1)
			notificate("session_expired");
		document.documentElement.setAttribute("data-bs-theme", "light");
		document.documentElement.style.setProperty(
			"color-scheme",
			"light",
			"important",
		);
	});
</script>

<svelte:window onpopstate={handlePopstate} />
<section
	class="bkg-owl login-page vw-100 vh-100 m-0 p-0 overflow-x-hidden"
	style="max-width: 100% !important;"
>
	<div class="d-flex w-100" style="align-items: center; min-height: 100%;">
		<div class="col-lg-4 mt-5 offset-lg-1">
			<div class="card card-outline">
				<div class="card-header text-center">
					<img
						src="/img/logo-vulseek-red-with-legend.png"
						alt="Vulseek by Securetia"
						class="brand-image logo-img logo-img-login"
					/>
				</div>
				<div class="card-body">
					{#if showLinkMessage}
						<div class="text-success">
							{$format("notifications.login_link_msg")}
						</div>
					{:else}
						<form
							role="form"
							novalidate
							onsubmit={preventDefault(handleSubmit)}
						>
							<div class="input-group mb-3">
								<input
									type="text"
									class="form-control"
									id="username"
									name="username"
									placeholder={$format("email_placeholder")}
									bind:value={$username.value}
									class:is-invalid={shouldShowFieldError(
										$username,
									)}
								/>
								<!-- BS5 FIX: input-group-text placed directly alongside input -->
								<span class="input-group-text">
									<span class="fas fa-envelope"></span>
								</span>
							</div>

							<!-- 5. WIDGET INVISIBLE DE ALTCHA -->
							<altcha-widget
								bind:this={widgetRef}
								challenge={`${API.url}/otac/captcha/challenge`}
								configuration={altchaConfig}
								onstatechange={handleCaptchaState}
								auto="onload"
							></altcha-widget>

							<div class="row mt-3">
								<div class="col-6">
									<!-- BS5 FIX: added w-100 to restore block behavior, kept your btn-block for CSS targeting -->
									<button
										id="loginSubmit"
										type="submit"
										class="btn btn-primary btn-block vlsk-bg-red"
										disabled={!isCaptchaVerified}
										class:opacity-50={!isCaptchaVerified}
										>{$format("buttons.login")}</button
									>
								</div>
							</div>
						</form>
						<div class="mt-3">
							<p class="mt-0 mb-0">
								<a href="/register" class="text-center"
									>{$format("buttons.create_account")}</a
								>
							</p>
						</div>
					{/if}
					{#if shouldShowFieldError($username)}
						<div class="text-danger mt-4">
							{$format("page.register.insert_valid_email")}
						</div>
					{/if}
					{#key error}
						{#if error}
							<div class="text-danger mt-4">{error}</div>
						{/if}
					{/key}
					{#if is_suspended}
						<div class="text-danger mt-4" style="font-size: 1rem;">
							{@html $format("errors.workspace_suspended")}
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
</section>
<Footer />

<style>
	.bkg-owl {
		background-color: white;
	}

	.logo-img.logo-img-login {
		max-width: 35%;
	}

	.vlsk-bg-red {
		background-color: rgb(251, 56, 59) !important;
		border-color: rgb(251, 56, 59) !important;
	}

	.card.card-outline {
		border-top: 3px solid rgb(251, 56, 59) !important;
		background-color: rgba(233, 236, 239, 0.25);
		box-shadow:
			0 0 1px rgba(0, 0, 0, 0.125),
			0 1px 3px rgba(0, 0, 0, 0.2);
	}

	.btn.btn-primary.btn-block {
		font-size: 1em;
		width: 180px;
	}

	.input-group input {
		height: 30px;
	}

	@media (min-width: 992px) {
		.bkg-owl {
			background-image: url("/img/owl.png");
			background-repeat: no-repeat;
			background-size: contain;
			background-position: right center;
			position: relative;
			width: 100%;
			background-color: transparent;
			max-height: 100%;
			left: 0px;
		}
	}

	@media (min-width: 1600px) {
		.card-header {
			padding: 1.75rem;
		}
		.card.card-outline .card-body {
			padding: 2rem;
		}
		.card-body > * {
			font-size: 1rem;
		}
		.btn.btn-primary.btn-block {
			font-size: 1rem;
		}
	}

	@media (min-width: 1800px) {
		.card-header {
			padding: 1.75rem;
		}
		.card.card-outline .card-body {
			padding: 3rem;
		}
		.card-body > * {
			font-size: 1.25rem;
		}
		.btn.btn-primary.btn-block {
			font-size: 1.25rem;
		}
		.input-group input {
			height: 40px;
		}
	}
</style>
